import {
  AdpDocument,
  DocType,
  isUserDocument,
  Part,
  UserDocument,
} from '@/types';

const SPACING_CLASS_08 = 'spacing-08';
const DELAY_PER_CHAR = 0.005;
// `\n`だけの文字列の場合に、spacingクラスを付与したdivを追加しないnodeName
const IGNORE_SPACING_NODE_NAMES: ReadonlyArray<Node['nodeName']> = [
  'UL',
  'OL',
  'TABLE',
];

/**
 * テキストを1文字ずつアニメーション遅延を付与したPart配列を生成する
 */
const createAnimatedTextParts = (
  text: string,
  startDelay: number,
  parentDomOrder: number,
): Part[] => {
  return text.split('').map((char, index) => ({
    type: 'text' as const,
    content: char,
    delay: startDelay + index * DELAY_PER_CHAR,
    parentDomOrder: parentDomOrder,
  }));
};

/**
 * 新引用形式文字列 ([^docType:id:pageNumber]) からソースのインデックスを検索する
 */
export const findSourceIndexForTypedCitation = (
  sources: Array<AdpDocument | UserDocument>,
  docType: DocType | 'user_document',
  id: string,
  pageNumber?: string,
): number => {
  const matchDocType = (source: AdpDocument | UserDocument) => {
    return source.doc_type === docType;
  };
  const matchId = (source: AdpDocument | UserDocument) => {
    if (isUserDocument(source)) return source.id === id;
    // NOTE: 公開情報はidが数値であるため、数値に変換する必要がある
    return (source as AdpDocument).id === parseInt(id, 10);
  };
  const matchPageNumber = (source: AdpDocument | UserDocument) => {
    return (
      pageNumber === undefined ||
      (isUserDocument(source) &&
        source.page_number !== undefined &&
        String(source.page_number) === pageNumber)
    );
  };
  let index = sources.findIndex(
    source =>
      matchDocType(source) && matchId(source) && matchPageNumber(source),
  );
  if (index === -1) {
    index = sources.findIndex(
      source => matchDocType(source) && matchId(source),
    );
  }
  return index;
};

/**
 * Surveyノードをパースする共通ロジック（アニメーション有無を切り替え可能）
 * @param node パース対象のNode
 * @param options パースオプション
 *   @param options.withAnimation アニメーション有無（true: 1文字ずつspanラップ＋delay付与, false: そのまま）
 *   @param options.accumulatedDelay 累積のアニメーション遅延時間（アニメーション時のみ使用）
 *   @param options.parentDomOrder 親要素のDOM順序
 *   @param options.ignoreNewlineOnlySpacing 改行文字列のみの場合にspacingクラス付きdivを追加しないフラグ
 *   @param options.sourceDocuments 引用ドキュメントのリスト
 * @returns Part型配列と（アニメーション時のみ）delay
 */
function _parseSurveyNode(
  node: Node,
  options: {
    withAnimation: boolean;
    accumulatedDelay?: number;
    parentDomOrder?: number;
    ignoreNewlineOnlySpacing?: boolean;
    sourceDocuments?: Array<AdpDocument | UserDocument>;
  } = { withAnimation: false },
): { parts: Part[]; delay: number } {
  const parts: Part[] = [];
  let currentDelay = options.accumulatedDelay ?? 0;
  let parentDomOrder = options.parentDomOrder ?? 1;
  const ignoreNewlineOnlySpacing = options.ignoreNewlineOnlySpacing ?? false;
  const sourceDocuments = options.sourceDocuments ?? [];

  node.childNodes.forEach((child: Node) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const citationRegex = /\[\^([a-z_]+):([0-9A-Z]+)(?::(\d+))?\]/g;
      let lastIndex = 0;
      let match;
      const text = child.nodeValue || '';

      if (text === '\n') {
        if (
          ignoreNewlineOnlySpacing ||
          isInsideIgnoreSpacingElement(child) ||
          isPreviousHeading(parts)
        )
          return;
        if (options.withAnimation) currentDelay += DELAY_PER_CHAR;
        parts.push({
          type: 'element',
          tagName: 'div',
          attributes: { class: SPACING_CLASS_08 },
          children: [],
          delay: options.withAnimation ? currentDelay : 0,
          parentDomOrder: parentDomOrder,
        });
        return;
      }

      while ((match = citationRegex.exec(text)) !== null) {
        if (lastIndex < match.index) {
          const partialText = text.slice(lastIndex, match.index);
          if (options.withAnimation) {
            const textParts = createAnimatedTextParts(
              partialText,
              currentDelay,
              parentDomOrder,
            );
            parts.push(...textParts);
            currentDelay += partialText.length * DELAY_PER_CHAR;
          } else {
            parts.push({
              type: 'text',
              content: partialText,
              delay: 0,
              parentDomOrder: parentDomOrder,
            });
          }
        }
        if (options.withAnimation) currentDelay += DELAY_PER_CHAR;

        // ソース引用部分をカスタムコンポーネントに置き換え
        const docType = match[1] as DocType | 'user_document';
        const idStr = match[2];
        const pageNumber = match[3];
        const sourceIndex = findSourceIndexForTypedCitation(
          sourceDocuments,
          docType,
          idStr,
          pageNumber,
        );
        const badgeNumber = sourceIndex >= 0 ? sourceIndex + 1 : 0;
        const sourceDocument = sourceDocuments[sourceIndex];
        if (sourceDocument) {
          parts.push({
            type: 'citationSourceBadge',
            badgeNumber,
            docType: docType,
            sourceDocument,
            delay: options.withAnimation ? currentDelay : 0,
            parentDomOrder: parentDomOrder,
          });
        }
        lastIndex = citationRegex.lastIndex;
      }

      if (lastIndex < text.length) {
        const remainingText = text.slice(lastIndex);
        if (options.withAnimation) {
          const textParts = createAnimatedTextParts(
            remainingText,
            currentDelay,
            parentDomOrder,
          );
          parts.push(...textParts);
          currentDelay += remainingText.length * DELAY_PER_CHAR;
        } else {
          parts.push({
            type: 'text',
            content: remainingText,
            delay: 0,
            parentDomOrder: parentDomOrder,
          });
        }
      }
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const element = child as Element;
      const childResult = _parseSurveyNode(element, {
        ...options,
        accumulatedDelay: options.withAnimation
          ? currentDelay + DELAY_PER_CHAR
          : 0,
        parentDomOrder,
      });
      if (options.withAnimation) currentDelay = childResult.delay;
      // Table要素はカスタムコンポーネントでwrap
      const type =
        element.nodeName.toLowerCase() === 'table'
          ? 'tableWithDownload'
          : 'element';
      parts.push({
        type,
        tagName: element.tagName.toLowerCase(),
        attributes: Array.from(element.attributes).reduce<{
          [key: string]: string;
        }>((attrs, attr) => {
          attrs[attr.name] = attr.value;
          return attrs;
        }, {}),
        children: childResult.parts,
        delay: options.withAnimation
          ? (options.accumulatedDelay ?? 0) + DELAY_PER_CHAR
          : 0,
        parentDomOrder,
      });
    }
    parentDomOrder++;
  });

  return { parts, delay: currentDelay };
}

/**
 * アニメーションありでSurveyノードをパース
 */
export const parseAndAnimateSurveyNode = (
  node: Node,
  accumulatedDelay = 0,
  parentDomOrder = 1,
  ignoreNewlineOnlySpacing = false,
  sourceDocuments: Array<AdpDocument | UserDocument> = [],
): { animatedParts: Part[]; animatedDelay: number } => {
  const { parts, delay } = _parseSurveyNode(node, {
    withAnimation: true,
    accumulatedDelay,
    parentDomOrder,
    ignoreNewlineOnlySpacing,
    sourceDocuments,
  });
  return { animatedParts: parts, animatedDelay: delay };
};

/**
 * アニメーションなしでSurveyノードをパース
 */
export const parseSurveyNode = (
  node: Node,
  ignoreNewlineOnlySpacing = false,
  sourceDocuments: Array<AdpDocument | UserDocument> = [],
): { parts: Part[] } => {
  const { parts } = _parseSurveyNode(node, {
    withAnimation: false,
    ignoreNewlineOnlySpacing,
    sourceDocuments,
  });
  return { parts };
};

/**
 * spacingクラスを付与したdivを追加したくない要素内か判定する
 */
const isInsideIgnoreSpacingElement = (node: Node): boolean => {
  let currentNode: Node | null = node; // 引数として渡された node を上書きしないように新しい変数を用意する
  while (currentNode) {
    if (IGNORE_SPACING_NODE_NAMES.includes(currentNode.nodeName)) return true;
    currentNode = currentNode.parentNode as Node;
  }
  return false;
};

/** 直前がh1やh2などの見出し要素か判定する */
const isPreviousHeading = (parts: Part[]): boolean => {
  if (parts.length === 0) {
    return false;
  }
  const lastPart = parts[parts.length - 1];
  return (
    lastPart.type === 'element' &&
    ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(lastPart.tagName)
  );
};
